import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "@/api/apiClient";
import { deleteDocument } from "@/api/documentApi";
import { createGraphFromDocuments } from "@/api/graphBuilderApi";

interface UseSourcesMutationsOptions {
	workspaceId?: string | null;
	selectedSources: Set<string>;
	openMenuId: string | null;
	onSourceToggle: (sourceId: string) => void;
	onJobStarted: (jobId: string) => void;
}

export function useSourcesMutations({
	workspaceId,
	selectedSources,
	openMenuId,
	onSourceToggle,
	onJobStarted,
}: UseSourcesMutationsOptions) {
	const queryClient = useQueryClient();
	const [quotaMessage, setQuotaMessage] = useState<string | null>(null);

	const deleteMutation = useMutation({
		mutationFn: (documentId: string) => deleteDocument(documentId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["documents"] });
			if (selectedSources.has(openMenuId ?? "")) {
				onSourceToggle(openMenuId ?? "");
			}
		},
	});

	const generateGraphMutation = useMutation({
		mutationFn: (documentIds: string[]) => createGraphFromDocuments({ documentIds, workspaceId: workspaceId ?? null }),
		onSuccess: (data) => {
			setQuotaMessage(null);
			onJobStarted(data.jobId);
		},
		onError: (error) => {
			if (error instanceof ApiError && error.status === 402) {
				setQuotaMessage("Today's free graph quota is already used up. Please come back tomorrow.");
				return;
			}
			setQuotaMessage(null);
			console.error("Failed to generate graph:", error);
			alert(`Failed to generate graph: ${error instanceof Error ? error.message : "Unknown error"}`);
		},
	});

	return { deleteMutation, generateGraphMutation, quotaMessage, setQuotaMessage };
}
